import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  hasLevels,
  LOCATION_TYPE_LABEL_HU,
  type FolderDetail,
  type LocationView,
} from '@persped/shared';
import { useState } from 'react';
import { useLocations, useMoveFolder } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Mappa áthelyezése (US-LOCATION-2).
 *
 * A spec 8. §-a szerint a célhely „vonalkóddal VAGY kereséssel"
 * választható — weben a keresés a természetes ág, ezért választólistát
 * adunk, nem vonalkód-mezőt. Raklap célhelynél a szint KÖTELEZŐ (ezt a DB
 * CHECK constraintje is kikényszeríti), ezért a szintválasztó csak akkor
 * jelenik meg, és amíg üres, a gomb tiltott.
 */
export function MoveFolderCard({ folder }: { folder: FolderDetail }) {
  const move = useMoveFolder();
  const locations = useLocations({ status: 'ACTIVE' });
  const [target, setTarget] = useState<LocationView | null>(null);
  const [level, setLevel] = useState('');

  // A szint NEM raklap-specifikus (D fázis): a polcnak is vannak szintjei, és
  // a `fla` CHECK-je mindkettőnél megköveteli. Ha itt csak a raklapot néznénk,
  // egy polcra való áthelyezés szintválasztó nélkül menne el, és a szerver
  // 422-vel dobná vissza — a felhasználó pedig nem tudná, mit hagyott ki.
  const needsLevel = target !== null && hasLevels(target.locationType);
  const levelRequired = needsLevel && level === '';
  const sameAsCurrent = target !== null && target.barcode === folder.currentLocation?.barcode;
  const canMove = target !== null && !levelRequired && !move.isPending;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Áthelyezés</Typography>

        {move.isError && <ProblemAlert error={move.error} />}
        {move.isSuccess && <Alert severity="success">Áthelyezve.</Alert>}

        <Autocomplete
          options={locations.data ?? []}
          value={target}
          onChange={(_, next) => {
            setTarget(next);
            setLevel('');
            move.reset();
          }}
          loading={locations.isPending}
          getOptionLabel={(l) => `${l.barcode} — ${l.name}`}
          groupBy={(l) => LOCATION_TYPE_LABEL_HU[l.locationType]}
          isOptionEqualToValue={(a, b) => a.barcode === b.barcode}
          renderInput={(params) => <TextField {...params} label="Célhely" />}
        />

        {needsLevel && target.levels && (
          <TextField
            select
            label="Szint"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            helperText="Raklapra és polcra helyezéskor a szint kötelező."
            fullWidth
          >
            {target.levels.map((l) => (
              <MenuItem key={l.levelNumber} value={String(l.levelNumber)}>
                {l.levelNumber}. szint — {l.folderCount} mappa
              </MenuItem>
            ))}
          </TextField>
        )}

        {sameAsCurrent && (
          <Alert severity="info">A mappa már ezen a lokáción van.</Alert>
        )}

        <Stack direction="row">
          <Button
            variant="contained"
            disabled={!canMove}
            onClick={() => {
              if (!target) return;
              move.mutate({
                barcode: folder.barcode,
                targetLocationBarcode: target.barcode,
                targetPalletLevel: level ? Number(level) : undefined,
              });
            }}
          >
            Áthelyezés
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
